import React, { useState, useEffect, useRef } from "react";
import { Modal, Header, Popup, Container, Card } from "semantic-ui-react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  dltDocumentFile,
  deleteSuccess,
  trainDocument,
  requestTrainSuccess,
} from "../../actions/dataConsoleAction";
import { getDocumentContentBlob, getDocumentPreviewSource } from "../../apis";
import Loader from "../../components/Loader";
import PDFViewer from "./PDFViewer";
import { set } from "react-hook-form";
import { pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = 
  `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;



const DatasetDocDetailsPage = ({
  columns,
  datas,
  docSettings,
  setUpdatedFiles,
  showToaster,
}) => {
  const dispatch = useDispatch();
  const { datasetNameParam } = useParams();
  const { deleteData, deleteDataStatus } = useSelector(
    (state) => state.dltFileReducer || {}
  );
  // Helper function to extract file extension
  const getFileExtension = (filename) => {
    return filename.split(".").pop().toLowerCase();
  };

  const { trainData, trainStatus } = useSelector(
    (state) => state.trainRequest || {}
  );
  const [page, setPage] = useState(0);
  const [deleteFileName, setdeleteFileName] = useState("");
  const [DeletePayload, setDeletePayload] = useState({});
  const [rowsPerPage, setRowsPerPage] = useState(
    parseInt(`${process.env.REACT_APP_ROWSPERPAGE}`)
  ); // Default rows per page
  const [openDeleteBox, setopenDeleteBox] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [deleteFlag, setdeleteFlag] = useState(false);
  const [loaderFlag, setloaderFlag] = useState(false);
  const [loginedUserName, setloginedUserName] = useState("");
  const userToken = useSelector((state) => state.auth.token);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [PreviewDocUrl, setPreviewDocUrl] = useState("");
  const previewObjectUrlRef = useRef(null);
  const [PreviewDocFilename, setPreviewDocFilename] = useState("");
  const [trainingIndex, setTrainingIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setloginedUserName(userToken.name);
  }, [userToken]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (deleteDataStatus === 200 && deleteFlag) {
      setUpdatedFiles(true);
      setdeleteFlag(false);
      setloaderFlag(false);
      showToaster(`${deleteFileName} Delete in progress. This will be completed in few minutes.`, "success");
      dispatch(deleteSuccess([]));
    }
    if (deleteFlag) {
      setUpdatedFiles(true);
    }
  }, [deleteData, deleteFlag, deleteDataStatus, dispatch]);

  useEffect(() => {
    if (trainStatus === 200) {
      setUpdatedFiles(true);
      setTrainingIndex(null);
      dispatch(requestTrainSuccess([]));
    }
  }, [trainData, trainStatus]);

  // Revoke the previous preview object URL whenever a new one is created or
  // the component unmounts, since these are only valid for this browser tab.
  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        window.URL.revokeObjectURL(previewObjectUrlRef.current);
      }
    };
  }, []);

  const handleEdit = async (value) => {
    try {
      const blob = await getDocumentContentBlob(value.datasetId, value.id);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = value.documentName;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleDelete = async (value) => {
    setdeleteFileName(value.documentName);
    setDeletePayload(value);
    setopenDeleteBox(true);
  };

  const handlePreviewDocument = async (value) => {
    try {
      const isMarkdown = value.documentName?.toLowerCase().endsWith(".md");

      if (isMarkdown) {
        // Markdown preview just needs the raw text (ReactMarkdown renders it
        // in one shot), so the full-fetch-then-blob-URL path is fine here.
        const blob = await getDocumentContentBlob(value.datasetId, value.id);
        if (previewObjectUrlRef.current) {
          window.URL.revokeObjectURL(previewObjectUrlRef.current);
        }
        const objectUrl = window.URL.createObjectURL(blob);
        previewObjectUrlRef.current = objectUrl;
        setPreviewDocFilename(value.documentName);
        setPreviewDocUrl(objectUrl);
        setIsModalOpen(true);
        return;
      }

      // For PDFs, hand react-pdf/pdf.js the authenticated content endpoint
      // directly instead of pre-fetching the whole file into a Blob first.
      // pdf.js then loads it itself via HTTP range requests, rendering the
      // first page as soon as it has enough bytes instead of waiting for the
      // entire file — the same progressive-load behavior the old direct
      // SAS-URL preview had, without exposing the storage account (public
      if (previewObjectUrlRef.current) {
        window.URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
      setPreviewDocFilename(value.documentName);
      setPreviewDocUrl(getDocumentPreviewSource(value.datasetId, value.id));
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error previewing document:", error);
    }
  };

  const actionDeleteDocument = async () => {
    setopenDeleteBox(false);
    setdeleteFlag(true);
    try {
      setloaderFlag(true);
      dispatch(dltDocumentFile(DeletePayload));
    } catch (error) {
      console.error("Error deleting data:", error);
    }
  };

  const handleClose = () => {
    setopenDeleteBox(false);
  };

  const getTimeAgo = (createdUTC) => {
    const today = moment().startOf("day");
    const createdDate = moment.utc(createdUTC).local().startOf("day");

    const timeDifference = today.diff(createdDate, "days");

    if (timeDifference === 0) {
      return "Updated today";
    } else if (timeDifference === 1) {
      return "Updated yesterday";
    } else {
      return `Updated ${timeDifference} days ago`;
    }
  };

  useEffect(() => {
    // Check if any document is in "Queued" or "Training" status
    const activeDocs = datas.filter(
      (doc) =>
        doc.trainingStatus === "Queued" ||
        doc.trainingStatus === "Training" ||
        doc.trainingStatus === "Deleting"
    );
  
    // If no document is pending training, do not set up the interval.
    if (activeDocs.length === 0) {
      return;
    }
  
    const intervalId = setInterval(() => {
      // Only trigger the API if the page is visible
      if (document.visibilityState === "visible") {
        setUpdatedFiles(true);
        // Alternatively, dispatch your API call here:
        // dispatch(fetchDocuments(datasetNameParam));
      }
    }, 10000); // 10 seconds
  
    // Clean up the interval when the effect dependencies change or the component unmounts
    return () => clearInterval(intervalId);
  }, [datas, setUpdatedFiles]);
  

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handletrainDocument = (value) => {
    const { blobPath, documentType, id, datasetId } = value;
    const settingsPayload = docSettings[0];
    const payload = {
      blobPath: blobPath,
      documentType: documentType,
      chunkSize: settingsPayload?.chunkSize ?? process.env.REACT_APP_CHUNK_SIZE,
      chunk:settingsPayload?.chunk ?? process.env.REACT_APP_CHUNK,
      chunkOverlap: settingsPayload?.chunkOverlap ?? process.env.REACT_APP_CHUNK_OVERLAP,
      docChunkEnabled: settingsPayload?.docChunkEnabled ?? process.env.REACT_APP_DOC_AS_CHUNK,
      embeddingDeployment:
        settingsPayload?.embeddingDeployment ??
        process.env.REACT_APP_EMBEDDING_DEPLOYMENT,
      embeddingModel:
        settingsPayload?.embeddingModel ??
        process.env.REACT_APP_EMBEDDING_MODEL,
      imageSummarizationPrompt:
        settingsPayload?.imageSummarizationPrompt ??
        process.env.REACT_APP_IMAGE_SUMMARIZATION_PROMPT,
      numList: settingsPayload?.numList ?? process.env.REACT_APP_NUM_LIST,
      trainingMethod: settingsPayload?.trainingMethod ?? process.env.REACT_APP_TRAINING_METHOD,
      trainingModel: settingsPayload?.trainingModel ?? process.env.REACT_APP_TRAINING_MODEL,
      serviceAgentEnabled: settingsPayload?.serviceAgentEnabled ?? process.env.REACT_APP_SERVICE_AGENT_ENABLED,
      docAsChunkEnabled: settingsPayload?.docAsChunkEnabled ?? process.env.REACT_APP_DOC_AS_CHUNK_ENABLED,
      pageAsChunkEnabled: settingsPayload?.pageAsChunkEnabled ?? process.env.REACT_APP_DOC_AS_CHUNK_ENABLED,
    };
    dispatch(trainDocument(payload, id, datasetId));
  };

  const totalItems = datas.length;

  const DocumentCard = ({
    document,
    extraction_type,
    fileExtension, // Accept the fileExtension prop
    onDownload,
    onDelete,
    createdUTC,
    createdName,
    trainingStatus,
    onPreviewDocument,
    trainDocument,
    retrainScheduler,
    isTraining,
  }) => {
    const isCSV = fileExtension === "csv";

    return (
      <Card fluid className="qac-card">
        <Card.Content>
          <Card.Header>
            <div className="mobile-dataconsole-document-card">
              <p className="document-icons-outer">
                <span>
                  {isTraining ? (
                    <svg
                      version="1.1"
                      id="L9"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                      x="0px"
                      y="0px"
                      viewBox="0 0 100 100"
                      enableBackground="new 0 0 0 0"
                      xmlSpace="preserve"
                      width="80px" // Set the width
                      height="80px" // Set the height
                      style={{
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        position: "absolute",
                        margin: "auto",
                      }}
                    >
                      <path
                        fill="#0053ed"
                        d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50"
                      >
                        <animateTransform
                          attributeName="transform"
                          attributeType="XML"
                          type="rotate"
                          dur="1s"
                          from="0 50 50"
                          to="360 50 50"
                          repeatCount="indefinite"
                        />
                      </path>
                    </svg>
                  ) : (
                    <>
                      {trainingStatus === "Queued" ? (
                        <span className="status-queued">
                          <Popup
                            content="Training Queued"
                            inverted
                            position="top center"
                            style={{ borderRadius: "15px" }}
                            trigger={
                              <img src="/images/queued.svg" alt="Trained" />
                            }
                          />
                        </span>
                      ) : trainingStatus === "Training" || trainingStatus === "Deleting" ? (
                        <span className="status-training">
                          <Popup
                            content={trainingStatus}
                            inverted
                            position="top center"
                            style={{ borderRadius: "15px" }}
                            trigger={
                              <svg
                                version="1.1"
                                id="L9"
                                xmlns="http://www.w3.org/2000/svg"
                                xmlnsXlink="http://www.w3.org/1999/xlink"
                                x="0px"
                                y="0px"
                                viewBox="0 0 100 100"
                                enableBackground="new 0 0 0 0"
                                xmlSpace="preserve"
                                width="40px" // Set the width
                                height="40px" // Set the height
                                style={{}}
                              >
                                <path
                                  fill="#0053ed"
                                  d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50"
                                >
                                  <animateTransform
                                    attributeName="transform"
                                    attributeType="XML"
                                    type="rotate"
                                    dur="1s"
                                    from="0 50 50"
                                    to="360 50 50"
                                    repeatCount="indefinite"
                                  />
                                </path>
                              </svg>
                            }
                          />
                        </span>
                      ) : trainingStatus === "Trained" ? (
                        <span className="status-trained">
                          <Popup
                            content="Trained"
                            inverted
                            position="top center"
                            style={{ borderRadius: "15px" }}
                            trigger={
                              <img src="/images/trained.svg" alt="Trained" />
                            }
                          />
                        </span>
                      ) : trainingStatus === "Failed" ? (
                        <span className="status-failed">
                          {" "}
                          <Popup
                            content="Training Failed"
                            inverted
                            position="top center"
                            style={{ borderRadius: "15px" }}
                            trigger={
                              <img src="/images/failed.svg" alt="Failed" />
                            }
                          />
                        </span>
                      ) : (
                        <span className="status-unknown">
                          {" "}
                          <Popup
                            content="Training Failed"
                            inverted
                            position="top center"
                            style={{ borderRadius: "15px" }}
                            trigger={
                              <img src="/images/failed.svg" alt="Failed" />
                            }
                          />
                        </span>
                      )}
                    </>
                  )}
                </span>
                {document}
              </p>
              <p>{extraction_type}</p>
            </div>
            {retrainScheduler != null && retrainScheduler != 0 && retrainScheduler != "" && retrainScheduler != undefined ?
              <div>
                <span style={{ marginLeft: "25px", marginTop: "5px", fontWeight: "500" }}>Runs every {retrainScheduler} hours </span>
              </div>
              : ""}

          </Card.Header>
        </Card.Content>
        <Card.Content extra className="qac-card-extra-content">
          <div className="mobile-dataconsole-actions">
            <p className="dataconsole-actions-view-details grey-color-text-card">
              <span>
                <Popup
                  content={new Date(createdUTC).toLocaleDateString()}
                  inverted
                  position="right center"
                  style={{ borderRadius: "15px" }}
                  trigger={<span>Uploaded by </span>}
                />
              </span>
              {createdName.length < 15 ? (
                createdName
              ) : (
                <Popup
                  content={createdName}
                  inverted
                  position="right center"
                  style={{ borderRadius: "15px" }}
                  trigger={<span>{createdName.slice(0, 15)}..</span>}
                />
              )}
            </p>
            <p className="dataconsole-actions-delete">
              <div className="user-card-icons grey-icons grid4">
              {trainingStatus == "Queued"  && (
                 <span></span>
              )}
              {trainingStatus === "Training" && (
                 <span></span>
              )}
              {!isCSV   ? (
                  <div>
                    {/* Conditionally render the Preview button based on file extension */}
                    <img
                      className="tableButton"
                      src="/images/Eye.svg"
                      alt="View Details"
                      style={{ cursor: "pointer" }}
                      data-cy="doc-preview-link"
                      onClick={onPreviewDocument}
                    />
                  </div>
                ) : (
                  <span></span>
                )}
                {trainingStatus === "Failed" || trainingStatus === "Trained" ? (
                  <div>
                    <Popup
                      content="Re-Train Document"
                      inverted
                      position="top center"
                      style={{ borderRadius: "15px" }}
                      trigger={
                        <img
                          className="tableButton"
                          src="/images/refresh-color.svg"
                          alt="Re-Train Document"
                          style={{ cursor: "pointer" }}
                          data-cy="doc-retrain-link"
                          onClick={trainDocument}
                        />
                      }
                    />
                  </div>
                ) : null}
                <div>
                  <img
                    className="tableButton"
                    src="/images/grey-download.svg"
                    alt="Edit"
                    style={{ cursor: "pointer" }}
                    data-cy="dataset-detail-link"
                    onClick={onDownload}
                  />
                </div>
                <div>
                  <img
                    className="tableButton"
                    src="/images/grey-delete.svg"
                    alt="Delete"
                    style={{ cursor: "pointer" }}
                    data-cy="dataset-delete-link"
                    onClick={onDelete}
                  />
                </div>
              </div>
              <div className="user-card-icons color-icons grid4">
              {trainingStatus == "Queued"  && (
                 <span></span>
              )}
              {trainingStatus === "Training" && (
                 <span></span>
              )}
                {!isCSV ? (
                  <div>
                    {/* Conditionally render the Preview button based on file extension */}
                    <Popup
                      content="Preview Document"
                      inverted
                      position="top center"
                      style={{ borderRadius: "15px" }}
                      trigger={
                        <img
                          className="tableButton"
                          src="/images/blue-eye.svg"
                          alt="View Details"
                          style={{ cursor: "pointer" }}
                          data-cy="doc-preview-link"
                          onClick={onPreviewDocument}
                        />
                      }
                    />
                  </div>
                ) : (
                  <span></span>
                )}
                {trainingStatus === "Failed" || trainingStatus === "Trained" ? (
                  <div>
                    <Popup
                      content="Re-Train Document"
                      inverted
                      position="top center"
                      style={{ borderRadius: "15px" }}
                      trigger={
                        <img
                          className="tableButton"
                          src="/images/refresh-color.svg"
                          alt="Re-Train Document"
                          style={{ cursor: "pointer" }}
                          data-cy="doc-retrain-link"
                          onClick={trainDocument}
                        />
                      }
                    />
                  </div>
                ) : null}
                <div>
                  <Popup
                    content="Download Document"
                    inverted
                    position="top center"
                    style={{ borderRadius: "15px" }}
                    trigger={
                      <img
                        className="tableButton"
                        src="/images/blue-download.svg"
                        alt="Edit"
                        style={{ cursor: "pointer" }}
                        data-cy="dataset-detail-link"
                        onClick={onDownload}
                      />
                    }
                  />
                </div>
                <div>
                  <Popup
                    content="Delete Document"
                    inverted
                    position="top center"
                    style={{ borderRadius: "15px" }}
                    trigger={
                      <img
                        className="tableButton"
                        src="/images/red-delete.svg"
                        alt="Delete"
                        style={{ cursor: "pointer" }}
                        data-cy="dataset-delete-link"
                        onClick={onDelete}
                      />
                    }
                  />
                </div>
              </div>
            </p>
          </div>
        </Card.Content>
      </Card>
    );
  };

  return (
    <>
      {loading ? (
        <div>
          <Loader />
        </div>
      ) : (
        <Container>
          <Card.Group itemsPerRow={3} stackable>
            {datas.length > 0
              ? datas.map((row, index) => (
                <DocumentCard
                  key={row.id}
                  document={row.documentName}
                  createdUTC={row.createdUTC}
                  createdName={row.createdBy.display_name}
                  trainingStatus={row.trainingStatus}
                  extraction_type={row.documentType}
                  fileExtension={getFileExtension(row.documentName)}
                  onDownload={() => handleEdit(row)}
                  onDelete={() => handleDelete(row)}
                  onPreviewDocument={() => handlePreviewDocument(row)}
                  trainDocument={() => {
                    handletrainDocument(row);
                    setTrainingIndex(index);
                  }}
                  retrainScheduler={row.retrainScheduler}
                  isTraining={trainingIndex === index}
                  className="mobile-document-tab-card"
                />
              ))
              : ""}
          </Card.Group>
        </Container>
      )}

      <PDFViewer
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        url={PreviewDocUrl}
        filename={PreviewDocFilename}
      />

      <Modal
        open={openDeleteBox}
        onClose={handleClose}
        size="tiny"
        className="add-document-model deletePopup"
      >
        <Modal.Content>
          <Header as="h3">Delete document</Header>
          <p>Are you sure you want to delete "{deleteFileName}"</p>
        </Modal.Content>
        <Modal.Actions>
          <button
            className="cancel-btn"
            onClick={handleClose}
            data-cy="delete-documents-cancel"
          >
            Cancel
          </button>
          <button
            className="add-btn"
            onClick={actionDeleteDocument}
            data-cy="delete-documents-confirm"
          >
            Delete
          </button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default DatasetDocDetailsPage;